import React, { useState, useEffect } from 'react';
import { X, Check, AlertCircle } from 'lucide-react';
import { useForm } from '../hooks/useForm';
import { leadValidationSchema } from '../utils/validation';
import { submitLead } from '../utils/api';

const RegisterModal = ({ isOpen, onClose, initialType }) => {
  const [type, setType] = useState(initialType);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const {
    values,
    errors,
    touched,
    isSubmitting,
    handleChange,
    handleBlur,
    handleSubmit,
    reset,
  } = useForm(
    {
      name: '',
      email: '',
      city: '',
      state: '',
      credential: type === 'instructor' ? 'yes' : undefined,
    },
    leadValidationSchema
  );

  // Reset form when modal opens/closes or type changes
  useEffect(() => {
    if (isOpen) {
      reset();
      setSubmitted(false);
      setSubmitError(null);
    }
  }, [isOpen, reset]);

  useEffect(() => {
    setType(initialType);
  }, [initialType]);

  const onSubmit = async (formData) => {
    setSubmitError(null);
    
    try {
      const leadData = { type, ...formData };
      await submitLead(leadData);
      
      setSubmitted(true);
      // Reset after 3 seconds
      setTimeout(() => {
        onClose();
        setSubmitted(false);
      }, 3000);
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitError('Erro ao enviar formulário. Por favor, tente novamente.');
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    await handleSubmit(onSubmit);
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content glass-panel" onClick={e => e.stopPropagation()}>
        <button className="close-btn" onClick={onClose}>
          <X size={20} />
        </button>

        {submitted ? (
          <div className="success-state">
            <div className="success-icon">
              <Check size={32} />
            </div>
            <h3>Obrigado pelo interesse!</h3>
            <p>Entraremos em contato assim que a plataforma estiver disponível na sua região.</p>
          </div>
        ) : (
          <>
            <div className="modal-header">
              <h2>Entre na lista de espera</h2>
              <p>Seja um dos primeiros a usar o AutoMestre.</p>
            </div>

            <div className="type-toggle">
              <button
                className={`toggle-btn ${type === 'student' ? 'active' : ''}`}
                onClick={() => setType('student')}
              >
                Sou Aluno
              </button>
              <button
                className={`toggle-btn ${type === 'instructor' ? 'active' : ''}`}
                onClick={() => setType('instructor')}
              >
                Sou Instrutor
              </button>
            </div>

            {submitError && (
              <div className="error-message">
                <AlertCircle size={16} />
                <span>{submitError}</span>
              </div>
            )}

            <form onSubmit={handleFormSubmit} className="register-form">
              <div className="form-group">
                <label>Nome Completo</label>
                <input 
                  type="text" 
                  name="name" 
                  value={values.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Seu nome completo"
                  className={errors.name && touched.name ? 'error' : ''}
                />
                {errors.name && touched.name && (
                  <span className="field-error">{errors.name}</span>
                )}
              </div>

              <div className="form-group">
                <label>Email</label>
                <input 
                  type="email" 
                  name="email" 
                  value={values.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="seu@email.com"
                  className={errors.email && touched.email ? 'error' : ''}
                />
                {errors.email && touched.email && (
                  <span className="field-error">{errors.email}</span>
                )}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Cidade</label>
                  <input 
                    type="text" 
                    name="city" 
                    value={values.city}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="São Paulo"
                    className={errors.city && touched.city ? 'error' : ''}
                  />
                  {errors.city && touched.city && (
                    <span className="field-error">{errors.city}</span>
                  )}
                </div>
                <div className="form-group">
                  <label>Estado</label>
                  <select 
                    name="state" 
                    value={values.state}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={errors.state && touched.state ? 'error' : ''}
                  >
                    <option value="">UF</option>
                    <option value="AC">AC</option>
                    <option value="AL">AL</option>
                    <option value="AP">AP</option>
                    <option value="AM">AM</option>
                    <option value="BA">BA</option>
                    <option value="CE">CE</option>
                    <option value="DF">DF</option>
                    <option value="ES">ES</option>
                    <option value="GO">GO</option>
                    <option value="MA">MA</option>
                    <option value="MT">MT</option>
                    <option value="MS">MS</option>
                    <option value="MG">MG</option>
                    <option value="PA">PA</option>
                    <option value="PB">PB</option>
                    <option value="PR">PR</option>
                    <option value="PE">PE</option>
                    <option value="PI">PI</option>
                    <option value="RJ">RJ</option>
                    <option value="RN">RN</option>
                    <option value="RS">RS</option>
                    <option value="RO">RO</option>
                    <option value="RR">RR</option>
                    <option value="SC">SC</option>
                    <option value="SP">SP</option>
                    <option value="SE">SE</option>
                    <option value="TO">TO</option>
                  </select>
                  {errors.state && touched.state && (
                    <span className="field-error">{errors.state}</span>
                  )}
                </div>
              </div>

              {type === 'instructor' && (
                <div className="form-group">
                  <label>Possui credencial do Detran?</label>
                  <select 
                    name="credential" 
                    value={values.credential || 'yes'}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  >
                    <option value="yes">Sim, já possuo</option>
                    <option value="processing">Em andamento</option>
                    <option value="no">Não, quero saber mais</option>
                  </select>
                </div>
              )}

              <button 
                type="submit" 
                className="btn-primary submit-btn"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Enviando...' : 
                  type === 'student' ? 'Quero descontos exclusivos' : 'Cadastrar como Parceiro'
                }
              </button>
            </form>
          </>
        )}
      </div>

      <style>{`
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.8);
          backdrop-filter: blur(4px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 100;
          padding: 1rem;
        }
        .modal-content {
          width: 100%;
          max-width: 480px;
          background: var(--color-surface);
          padding: 2rem;
          position: relative;
          box-shadow: var(--shadow-lg);
          border: 1px solid var(--color-border);
        }
        .close-btn {
          position: absolute;
          top: 1rem;
          right: 1rem;
          background: transparent;
          color: var(--color-text-muted);
          padding: 0.5rem;
          border-radius: 50%;
        }
        .close-btn:hover {
          background: rgba(255, 255, 255, 0.1);
          color: var(--color-text-main);
        }
        .modal-header {
          text-align: center;
          margin-bottom: 1.5rem;
        }
        .modal-header h2 {
          font-size: 1.5rem;
          margin-bottom: 0.5rem;
        }
        .modal-header p {
          color: var(--color-text-muted);
          font-size: 0.875rem;
        }
        .type-toggle {
          display: flex;
          background: rgba(255, 255, 255, 0.05);
          padding: 0.25rem;
          border-radius: 0.5rem;
          margin-bottom: 2rem;
        }
        .toggle-btn {
          flex: 1;
          padding: 0.5rem;
          background: transparent;
          color: var(--color-text-muted);
          border-radius: 0.25rem;
          font-weight: 500;
          font-size: 0.875rem;
        }
        .toggle-btn.active {
          background: var(--color-surface-hover);
          color: var(--color-text-main);
          box-shadow: var(--shadow-sm);
        }
        .register-form {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }
        .form-group {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }
        .form-row {
          display: grid;
          grid-template-columns: 2fr 1fr;
          gap: 1rem;
        }
        label {
          font-size: 0.875rem;
          font-weight: 500;
          color: var(--color-text-muted);
        }
        input, select {
          padding: 0.75rem;
          border-radius: 0.5rem;
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid var(--color-border);
          color: var(--color-text-main);
          outline: none;
          font-family: inherit;
        }
        input:focus, select:focus {
          border-color: var(--color-primary);
          box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
        }
        .submit-btn {
          width: 100%;
          margin-top: 1rem;
        }
        
        .success-state {
          text-align: center;
          padding: 2rem 0;
        }
        .success-icon {
          width: 64px;
          height: 64px;
          background: rgba(16, 185, 129, 0.1);
          color: #10B981;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 1.5rem;
        }
        .success-state h3 {
          font-size: 1.5rem;
          margin-bottom: 0.5rem;
        }
        .success-state p {
          color: var(--color-text-muted);
        }
      `}</style>
    </div>
  );
};

export default RegisterModal;
